"use client";

/**
 * @file src/components/ui/RotatingMessages/index.tsx
 * @description Reusable component that displays rotating messages with typewriter or swipe animations
 * Messages rotate every 4 seconds after animation completes
 *
 * @example
 * ```tsx
 * import { RotatingMessages } from "@/components/ui/RotatingMessages";
 * import { LOADING_MESSAGES } from "@/lib/utils/loadingMessages";
 *
 * <RotatingMessages 
 *   messages={LOADING_MESSAGES}
 *   animationStyle="typewriter"
 *   displayTimeMs={4000}
 * />
 * ```
 */

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface RotatingMessagesProps {
  /** Array of messages to rotate through */
  messages: string[];
  /** Animation style: 'typewriter' for character-by-character, 'swipeRight' for sliding transitions */
  animationStyle?: 'typewriter' | 'swipeRight';
  /** Time to display each message before rotating (ms) - timer starts AFTER animation completes */
  displayTimeMs?: number;
}

export function RotatingMessages({
  messages,
  animationStyle = 'typewriter',
  displayTimeMs = 4000,
}: RotatingMessagesProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);

  // Shuffle messages once on mount using a lazy initializer (outside render cycle)
  const [shuffledMessages] = useState<string[]>(() => {
    const shuffled = [...messages];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled.length > 0 ? shuffled : messages;
  });

  // Handle message rotation after animation completes
  useEffect(() => {
    if (!isAnimating) {
      // Animation has completed, start the display timer
      const timer = setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % shuffledMessages.length);
        setIsAnimating(true);
      }, displayTimeMs);

      return () => clearTimeout(timer);
    }
  }, [isAnimating, displayTimeMs, shuffledMessages.length]);

  if (shuffledMessages.length === 0) {
    return null;
  }

  const currentMessage = shuffledMessages[currentIndex];

  return (
    <div className="w-full max-w-lg min-h-8 flex items-center justify-center px-4">
      <AnimatePresence mode="wait">
        {animationStyle === 'typewriter' ? (
          <TypewriterMessage
            key={`${currentIndex}-typewriter`}
            text={currentMessage}
            onAnimationComplete={() => setIsAnimating(false)}
          />
        ) : (
          <SwipeRightMessage
            key={`${currentIndex}-swipe`}
            text={currentMessage}
            onAnimationComplete={() => setIsAnimating(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * Helper to pre-calculate starting indices for each word cleanly.
 * This is defined outside the React component to satisfy the React Compiler.
 */
function getWordsWithIndices(text: string) {
  const words = text.split(' ');
  let accumIndex = 0;
  return words.map((word) => {
    const startIdx = accumIndex;
    accumIndex += word.length + 1; // +1 for the space
    return {
      word,
      startIdx,
    };
  });
}

/**
 * Typewriter animation: Characters appear one-by-one very quickly
 */
function TypewriterMessage({
  text,
  onAnimationComplete,
}: {
  text: string;
  onAnimationComplete: () => void;
}) {
  const wordsWithIndices = getWordsWithIndices(text);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onAnimationComplete={onAnimationComplete}
      className="text-md font-medium text-foreground text-center min-h-8"
    >
      <div className="flex flex-wrap justify-center gap-1">
        {wordsWithIndices.map(({ word, startIdx }, wordIdx) => {
          const wordChars = word.split('');

          return (
            <div key={wordIdx} className="whitespace-nowrap flex gap-0">
              {wordChars.map((char, charIdx) => (
                <motion.span
                  key={`${wordIdx}-${charIdx}`}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{
                    duration: 0.05,
                    delay: (startIdx + charIdx) * 0.02,
                    ease: "easeInOut",
                  }}
                >
                  {char === ' ' ? '\u00A0' : char}
                </motion.span>
              ))}
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}

/**
 * Swipe right animation: Current message slides out left, new message slides in from left
 */
function SwipeRightMessage({
  text,
  onAnimationComplete,
}: {
  text: string;
  onAnimationComplete: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -40 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 40 }}
      transition={{
        duration: 0.4,
        ease: "easeInOut",
      }}
      onAnimationComplete={onAnimationComplete}
      className="text-lg font-medium text-foreground text-center min-h-8 absolute"
    >
      {text}
    </motion.div>
  );
}
